import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateAssignmentFormDTO,
  UpdateAssignmentFormDTO,
} from "@/modules/assign-position/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { assignmentsApi } from "./client";

export const assignmentsKeys = {
  all: ["assignments"] as const,
  users: (q: Partial<OffsetPageQueryDTO>) =>
    ["assignments", "users", q] as const,
  detail: (id: string) => ["assignments", "detail", id] as const,
  userHistory: (userId: string) =>
    ["assignments", "userHistory", userId] as const,
};

export function useUsersWithAssignmentsQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: assignmentsKeys.users(q),
    queryFn: () => assignmentsApi.listUsers(q),
  });
}

export function useAssignmentQuery(id: string) {
  return useQuery({
    queryKey: assignmentsKeys.detail(id),
    queryFn: () => assignmentsApi.get(id),
    enabled: !!id,
  });
}

export function useUserHistoryQuery(userId: string) {
  return useQuery({
    queryKey: assignmentsKeys.userHistory(userId),
    queryFn: () => assignmentsApi.getUserHistory(userId),
    enabled: !!userId,
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAssignmentFormDTO) =>
      assignmentsApi.create(input),
    onSuccess: () => {
      toast.success("ກໍາຫນົດຕໍາແໜ່ງສໍາເລັດ");
      qc.invalidateQueries({ queryKey: assignmentsKeys.all });
      qc.invalidateQueries({ queryKey: ["positions"] });
    },
    onError: () => toast.error("ກໍາຫນົດຕໍາແໜ່ງລົ້ມເຫຼວ"),
  });
}

export function useUpdateAssignment(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAssignmentFormDTO) =>
      assignmentsApi.update(userId, input),
    onSuccess: () => {
      toast.success("ປ່ຽນຕໍາແໜ່ງສໍາເລັດ");
      qc.invalidateQueries({ queryKey: assignmentsKeys.userHistory(userId) });
      qc.invalidateQueries({ queryKey: assignmentsKeys.all });
      qc.invalidateQueries({ queryKey: ["positions"] });
    },
    onError: () => toast.error("ປ່ຽນຕໍາແໜ່ງລົ້ມເຫຼວ"),
  });
}

export function useClearAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      assignmentsApi.clearCurrentAssignment(userId),
    onSuccess: (_, userId) => {
      toast.success("ເອົາຕໍາແໜ່ງອອກສໍາເລັດ");
      qc.invalidateQueries({ queryKey: assignmentsKeys.userHistory(userId) });
      qc.invalidateQueries({ queryKey: assignmentsKeys.all });
      qc.invalidateQueries({ queryKey: ["positions"] });
    },
    onError: (e) =>
      toast.error(
        e instanceof Error ? e.message : "ເອົາຕໍາແໜ່ງອອກລົ້ມເຫຼວ",
      ),
  });
}
